import { Link } from 'react-router-dom';
import { type ReactNode } from 'react';
import { MarketingShell } from '../components/MarketingShell';

// Public-facing Schools / Centres landing. UK-focused (Pupil Premium, JCQ,
// SENCOs, High Needs Funding). When MDT goes international we add per-country
// pages with the same shape but with the local equivalents (Title I funding in
// the US, Equity Funding in NZ, etc.).
//
// Targeted at SENCOs / Inclusion Leads, not headteachers. They are the people
// who pick the tool, file for funding, and run access arrangements.
export function SchoolsScreen() {
  return (
    <MarketingShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero */}
        <header>
          <p className="uppercase tracking-[0.2em] text-xs text-accent font-semibold">
            For UK schools and centres
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            An access arrangement, a dyslexia accommodation, and an exam-aligned tool
            in one.
          </h1>
          <p className="mt-4 text-ink/65 leading-relaxed">
            My Dyslexia Tools is the same product your pupils already use at home.
            With a school licence you can issue seats to the pupils who need it most
            (Pupil Premium, EHCP, SEND register, exam access arrangement candidates)
            and use it in class AND in real exams.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@mydyslexiatools.com?subject=School%20pilot%20-%20My%20Dyslexia%20Tools&body=Hello%2C%20we%27d%20like%20to%20pilot%20My%20Dyslexia%20Tools%20at%20our%20school.%0A%0ASchool%20name%3A%20%0ASENCO%2FInclusion%20Lead%20name%3A%20%0AApprox%20number%20of%20pupils%20we%27d%20want%20to%20cover%3A%20%0AKey%20stage%3A%20%0AAny%20questions%3A%20%0A"
              className="inline-flex items-center justify-center bg-accent text-white px-7 py-3 rounded-full font-medium hover:brightness-105 transition"
            >
              Request a free 30-day pilot
            </a>
            <Link
              to="/exam-mode"
              className="inline-flex items-center justify-center text-ink/70 hover:text-ink px-5 py-3 text-sm transition"
            >
              See how Exam Mode aligns with JCQ &nbsp;&rarr;
            </Link>
          </div>
        </header>

        {/* Three reasons it actually works for schools */}
        <section className="mt-12 grid sm:grid-cols-3 gap-4">
          <Card
            badge="01"
            title="JCQ-aligned Exam Mode"
            body="Locked attempt mode with append-only audit log. AI helpers off, no internet lookups, no spell-check, no word prediction, no dictionary. The same product the pupil uses at home, locked down for the paper they're sitting."
          />
          <Card
            badge="02"
            title="Reduces 1:1 reader / scribe load"
            body="Pupils who would normally need an adult to read every question and write every answer can complete the worksheet independently. Frees up TA time across the SEN cohort, not just exam season."
          />
          <Card
            badge="03"
            title="Trained on dyslexia, not retrofitted"
            body="Built by a parent of a child in the 1st centile for writing. Lexend Deca throughout, calm pacing, no walls of text, accommodations baked in rather than added later."
          />
        </section>

        {/* Funding eligibility */}
        <section className="mt-12 bg-white border border-ink/10 rounded-3xl p-8">
          <p className="uppercase tracking-[0.18em] text-xs text-accent font-semibold">
            Funded by
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">
            Pupil Premium and High Needs Funding eligible
          </h2>
          <p className="mt-3 text-ink/70 leading-relaxed">
            Schools regularly purchase assistive tech for dyslexia using existing per-pupil
            funding rather than the central SEN budget. My Dyslexia Tools is a textbook fit.
          </p>
          <ul className="mt-5 space-y-3 text-sm text-ink/80">
            <Bullet>
              <strong>Pupil Premium</strong> &ndash; the ~£1,480 (primary) / ~£1,055
              (secondary) per-pupil grant for children currently eligible for Free School Meals,
              or eligible at any point in the last six years (Ever 6 FSM), plus Looked After
              Children, post-LAC, and Service Children. Closing the attainment gap counts.
            </Bullet>
            <Bullet>
              <strong>High Needs Funding</strong> &ndash; the per-EHCP top-up paid by the local
              authority. SENCOs typically use this to buy assistive tech (Read&amp;Write,
              ClaroRead, etc). My Dyslexia Tools is a direct alternative at a fraction of the
              cost.
            </Bullet>
            <Bullet>
              <strong>Recovery Premium</strong> &ndash; the catch-up successor funding, still
              live for the 2025/26 academic year in most local authorities.
            </Bullet>
            <Bullet>
              <strong>SEN budget &ndash; Element 2</strong> &ndash; the £6,000 notional SEN
              budget each school holds. The first port of call for in-class support tools.
            </Bullet>
          </ul>
          <p className="mt-5 text-xs text-ink/55">
            We can provide a short justification statement for funding bids on request.
          </p>
        </section>

        {/* What schools get */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">What a school licence includes</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <Feature title="Bulk pupil onboarding">
              Add pupils by email or CSV. They get an invite email branded as your school.
              No app store accounts. Works on any phone, tablet, or laptop.
            </Feature>
            <Feature title="Seat assignment + revocation">
              Move a seat from one pupil to another at any point in the academic year
              (e.g. when a Year 11 leaves and a new EHCP arrives). No charge for seat moves.
            </Feature>
            <Feature title="SENCO dashboard">
              See usage at a glance: how many worksheets each pupil has done, how many
              minutes of reader / scribe time it has replaced.
            </Feature>
            <Feature title="Exam Mode audit logs">
              Append-only timestamped logs of every action inside a locked exam attempt.
              Downloadable as CSV. Hand to your exams officer if challenged by an awarding body.
            </Feature>
            <Feature title="No personal data on the device">
              Worksheet images and answers stay on the pupil's device. No identifying
              data goes to our servers. ICO Children's Code aligned.
            </Feature>
            <Feature title="Invoice with PO number">
              Standard 30 / 60-day BACS payment terms. We send a single invoice, your bursar
              pays it. No card on file required.
            </Feature>
          </div>
        </section>

        {/* Licensing model - no public price, sent on enquiry */}
        <section className="mt-12 bg-warm/60 border border-amber-300 rounded-3xl p-8">
          <p className="uppercase tracking-[0.18em] text-xs text-accent font-semibold">
            School licence
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight">
            Per pupil, per academic year
          </h2>
          <p className="mt-3 text-ink/70 leading-relaxed">
            Minimum 5 seats. Renews each September. Move seats between pupils throughout
            the year at no extra cost. No setup fee, no per-document charges, no usage caps.
          </p>
          <p className="mt-3 text-sm text-ink/65 leading-relaxed">
            We price deliberately under the threshold that lets a SENCO order without
            going through full procurement, and well below incumbent suites
            (Read&amp;Write, ClaroRead). The individual app is free for parents and pupils
            outside a school deployment &mdash; the licence is what gets you bulk
            onboarding, audit logs, SENCO dashboard, and exam-mode oversight.
          </p>
          <p className="mt-5 text-xs text-ink/55">
            Full per-pupil quote in the pilot response &mdash; request a pilot below.
          </p>
        </section>

        {/* Free pilot */}
        <section className="mt-12 bg-white border border-ink/10 rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Start with a free 30-day pilot</h2>
          <p className="mt-3 text-ink/70 leading-relaxed max-w-xl mx-auto">
            Issue up to 30 seats to your SEN cohort, in class and at home, for a full month.
            Full feature set. No card. If it works for your pupils, you switch to a paid
            licence in September. If it doesn't, you walk away.
          </p>
          <a
            href="mailto:support@mydyslexiatools.com?subject=School%20pilot%20-%20My%20Dyslexia%20Tools&body=Hello%2C%20we%27d%20like%20to%20pilot%20My%20Dyslexia%20Tools%20at%20our%20school.%0A%0ASchool%20name%3A%20%0ASENCO%2FInclusion%20Lead%20name%3A%20%0AApprox%20number%20of%20pupils%20we%27d%20want%20to%20cover%3A%20%0AKey%20stage%3A%20%0AAny%20questions%3A%20%0A"
            className="inline-flex items-center justify-center bg-accent text-white px-7 py-3 rounded-full font-medium hover:brightness-105 transition mt-6"
          >
            Request a pilot
          </a>
          <p className="mt-3 text-xs text-ink/45">support@mydyslexiatools.com</p>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">FAQs</h2>
          <div className="mt-4 space-y-3">
            <Faq q="Will the awarding bodies accept Exam Mode as an access arrangement?">
              Exam Mode is designed to align with the JCQ rules for computer reader and
              scribe access arrangements: no internet lookups, no spell-check, no word
              prediction, no dictionary, no AI rephrasing. We are in the process of
              registering for the relevant formal approvals. In the meantime each centre
              is responsible for confirming the arrangement is permitted for the specific
              paper. Read the full policy at <Link to="/exam-mode" className="text-accent underline">/exam-mode</Link>.
            </Faq>
            <Faq q="What about Read & Write or ClaroRead?">
              They are excellent suites, but they are built for desktops and they cost
              &pound;40&ndash;&pound;50 per pupil per year. We are mobile-first, dyslexia-first,
              and priced under &pound;30 so a SENCO can issue a few seats without going
              through procurement.
            </Faq>
            <Faq q="Can pupils use their existing personal sign-in?">
              Yes. A pupil who has a personal MDT account can be assigned a school seat;
              the school covers their subscription while it is active, and their personal
              worksheets remain theirs. If they leave the school the seat is reclaimed
              but their data stays with their account.
            </Faq>
            <Faq q="What about safeguarding and ICO Children's Code?">
              Worksheet images never leave the device. Answers are stored locally in the
              pupil's browser or app. We log anonymous usage events only (no question text,
              no answer text, no photo) and pupils can opt out from settings. ICO
              Children's Code aligned. Our DPO contact is in the privacy policy.
            </Faq>
            <Faq q="Do you offer training for SENCOs?">
              Yes &ndash; a free 30-minute video call for any school that signs up, walking
              through the SENCO dashboard, Exam Mode, and how to brief invigilators. Email
              us to book.
            </Faq>
            <Faq q="What happens at the end of the academic year?">
              Seats renew automatically on 1 September unless you tell us not to. We email
              you in July with a renewal summary so you can review and adjust before the
              new term.
            </Faq>
          </div>
        </section>

        {/* Final CTA */}
        <div className="mt-14 text-center">
          <Link
            to="/exam-mode"
            className="inline-flex items-center justify-center bg-white border border-accent/30 text-accent px-6 py-2.5 rounded-full text-sm font-medium hover:bg-accent/5 transition"
          >
            Read the Exam Mode policy
          </Link>
        </div>
      </div>
    </MarketingShell>
  );
}

function Card({ badge, title, body }: { badge: string; title: string; body: string }) {
  return (
    <div className="bg-white border border-ink/10 rounded-2xl p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold">{badge}</p>
      <h3 className="mt-2 text-lg font-semibold tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-ink/70 leading-relaxed">{body}</p>
    </div>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}

function Feature({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-white border border-ink/10 rounded-2xl p-4">
      <p className="text-sm font-medium text-ink flex items-center gap-2">
        <BulletMark />
        {title}
      </p>
      <p className="mt-1 text-xs text-ink/65 leading-relaxed pl-7">{children}</p>
    </div>
  );
}

// Brand-mark bullet (the favicon squircle-with-dot) used in front of each
// "What a school licence includes" feature title.
function BulletMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="flex-shrink-0"
    >
      <rect x="3" y="3" width="58" height="58" rx="16" fill="#0369a1" />
      <circle cx="45" cy="19" r="7.5" fill="#FAFAF8" />
    </svg>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="bg-white border border-ink/10 rounded-2xl p-5 group">
      <summary className="font-medium text-ink cursor-pointer list-none flex items-center justify-between text-sm">
        {q}
        <span className="text-ink/40 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
      </summary>
      <div className="mt-3 text-sm text-ink/70 leading-relaxed">{children}</div>
    </details>
  );
}
